import {
  format,
  parseISO,
  isSameDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  getDay,
  add,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { ScheduledActivity, ActivityByDate } from './types';

const allDayDefaultStartTime = '8:00';

const millisecondsInDay = 24 * 60 * 60 * 1000;

const getCommonEventAttributes = (event: any) => {
  return {
    id: event.id,
    name: event.title,
    isCalendarEvent: true,
    activityId: event.activity_id,
    url: event.url,
    originalEvent: event,
    calendarName: event.source?.calendar?.name,
  };
};

/**
 * Groups activities by their due date
 */
export const groupActivitiesByDate = (
  activities: ScheduledActivity[],
  timezone: string,
  events: any[]
): ActivityByDate => {
  const activitiesMap = activities.reduce<ActivityByDate>((acc, activity) => {
    const activityDate = parseISO(activity.dueDatetime);
    const dateKey = formatInTimeZone(activityDate, timezone, 'yyyy-MM-dd');

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }

    acc[dateKey].push(activity);
    return acc;
  }, {});

  const activitiesUnsorted = events.reduce<any>((acc, event) => {
    const dateKey = formatInTimeZone(
      new Date(event.start_time),
      timezone,
      'yyyy-MM-dd'
    );

    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }

    acc[dateKey].push({
      ...getCommonEventAttributes(event),
      date: event.start_time,
      dueDatetime: event.start_time,
    });

    if (event.all_day) {
      const dayCountForEvent = Math.ceil(
        (event.end_time - event.start_time) / millisecondsInDay
      );

      for (let i = 1; i < dayCountForEvent; i++) {
        const newDate = addDays(new Date(event.start_time), i);
        const newDateKey = formatInTimeZone(newDate, timezone, 'yyyy-MM-dd');

        if (!acc[newDateKey]) {
          acc[newDateKey] = [];
        }

        acc[newDateKey].push({
          ...getCommonEventAttributes(event),
          date: newDate.getTime(),
          dueDatetime: newDate.getTime(),
          isSpanning: true,
        });
      }
    }

    return acc;
  }, activitiesMap);

  return Object.keys(activitiesUnsorted).reduce<ActivityByDate>(
    (acc, dateKey) => {
      const sortedActivities = activitiesUnsorted[dateKey]
        .filter((activity: any) => {
          if (activity.activityId) {
            const matchingActivity = activities.find((act) => {
              return act.id === activity.activityId && !act.isCalendarEvent;
            });

            if (matchingActivity) {
              return false;
            }
          }

          return true;
        })
        .sort((a: any, b: any) => {
          return (
            new Date(a.dueDatetime).getTime() -
            new Date(b.dueDatetime).getTime()
          );
        });

      acc[dateKey] = sortedActivities;
      return acc;
    },
    {}
  );
};

/**
 * Gets activities for a specific date
 */
export const getActivitiesForDate = (
  date: Date,
  activitiesByDate: ActivityByDate
): ScheduledActivity[] => {
  const dateKey = format(date, 'yyyy-MM-dd');
  return activitiesByDate[dateKey] || [];
};

const addMinutesToTime = (
  dateKey: string,
  hours: number,
  minutes: number,
  addMinutes: number
) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day, hours, minutes);
  const result = add(dateObj, { minutes: addMinutes });

  return result;
};

const activityIntervalMinutes = 30;

const getDateTimeParts = (date: Date, timezone: string) => {
  const dateKey = formatInTimeZone(date, timezone, 'yyyy-MM-dd');

  // Round to 30-minute intervals - extract hours/minutes in correct timezone
  const hours = parseInt(formatInTimeZone(date, timezone, 'H'));
  const minutes = parseInt(formatInTimeZone(date, timezone, 'm'));
  const roundedMinutes =
    minutes < activityIntervalMinutes ? '00' : `${activityIntervalMinutes}`;
  const timeKey = `${hours}:${roundedMinutes}`;

  const key = `${dateKey}-${timeKey}`;

  return { key, dateKey, hours, minutes };
};

/**
 * Groups activities by date and time slot (for day/week views)
 * Returns a lookup map: `{date-time: activities[]}`
 */
export const groupActivitiesByDateTime = (
  activities: ScheduledActivity[],
  timezone: string,
  events: any[]
): Record<string, ScheduledActivity[]> => {
  const activitiesMap = activities.reduce<Record<string, ScheduledActivity[]>>(
    (acc, activity) => {
      const activityDate = parseISO(activity.dueDatetime);
      const { key, dateKey, hours, minutes } = getDateTimeParts(
        activityDate,
        timezone
      );

      const minutesDuration = activity.activityObject.calendarSyncDuration || 0;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(activity);

      // Check how many intervals of 30 minutes are needed to cover the activity duration
      let additionalIntervals = !minutesDuration
        ? 0
        : Math.floor(minutesDuration / activityIntervalMinutes);

      if (
        minutesDuration % activityIntervalMinutes === 0 &&
        additionalIntervals > 0
      ) {
        additionalIntervals -= 1;
      }

      for (let i = 1; i <= additionalIntervals; i++) {
        const numMinutesToAdd = i * activityIntervalMinutes;

        const newDate = addMinutesToTime(
          dateKey,
          hours,
          minutes,
          numMinutesToAdd
        );

        const { key } = getDateTimeParts(newDate, timezone);

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push({
          ...activity,
          calculationDate: newDate.getTime(),
          // Mark as spanning activity - i.e. not starting at this time slot
          isSpanning: true,
        });
      }

      return acc;
    },
    {}
  );

  const activitiesUnsorted = events.reduce<any>((acc, event) => {
    const activityDate = new Date(event.start_time);

    let { key, dateKey, hours, minutes } = getDateTimeParts(
      activityDate,
      timezone
    );

    let minutesDuration = (event.end_time - event.start_time) / 1000 / 60;

    let totalDays = 1;
    if (event.all_day && !event.busy) {
      hours = 8;
      minutes = 0;
      key = `${dateKey}-${allDayDefaultStartTime}`;

      minutesDuration = 0;

      totalDays = Math.ceil(
        (event.end_time - event.start_time) / millisecondsInDay
      );
    } else if (event.all_day && event.busy) {
      const dayCountForBusyEvent = Math.ceil(
        (event.end_time - event.start_time) / millisecondsInDay
      );

      minutesDuration = 24 * 60 * dayCountForBusyEvent;
    }

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push({
      ...getCommonEventAttributes(event),
      date: event.start_time,
      dueDatetime: event.start_time,
      calculationDate: event.start_time,
      allDay: event.all_day,
    });

    if (totalDays > 1 && event.all_day && !event.busy) {
      for (let i = 1; i < totalDays; i++) {
        const newDate = addDays(activityDate, i);
        const { dateKey } = getDateTimeParts(newDate, timezone);

        const fullKey = `${dateKey}-${allDayDefaultStartTime}`;

        if (!acc[fullKey]) {
          acc[fullKey] = [];
        }

        acc[fullKey].push({
          ...getCommonEventAttributes(event),
          date: newDate.getTime(),
          dueDatetime: newDate.getTime(),
          calculationDate: newDate.getTime(),
          allDay: event.all_day,
          isSpanning: true,
        });
      }
    }

    // Check how many intervals of 30 minutes are needed to cover the activity duration
    let additionalIntervals = Math.floor(
      minutesDuration / activityIntervalMinutes
    );

    if (
      minutesDuration % activityIntervalMinutes === 0 &&
      additionalIntervals > 0
    ) {
      additionalIntervals -= 1;
    }

    for (let i = 1; i <= additionalIntervals; i++) {
      const numMinutesToAdd = i * activityIntervalMinutes;

      const newDate = addMinutesToTime(
        dateKey,
        hours,
        minutes,
        numMinutesToAdd
      );

      const { key } = getDateTimeParts(newDate, timezone);

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push({
        ...getCommonEventAttributes(event),
        date: event.start_time,
        calculationDate: newDate.getTime(),
        dueDatetime: event.start_time,
        isSpanning: true,
      });
    }

    return acc;
  }, activitiesMap);

  return Object.keys(activitiesUnsorted).reduce<
    Record<string, ScheduledActivity[]>
  >((acc, key) => {
    // Sort activities in each time slot by dueDatetime
    const sortedActivities = activitiesUnsorted[key]
      .filter((activity: any) => {
        if (activity.activityId) {
          const matchingActivity = activities.find((act) => {
            return act.id === activity.activityId && !act.isCalendarEvent;
          });

          if (matchingActivity) {
            return false;
          }
        }

        return true;
      })
      .sort((a: any, b: any) => {
        if (a.isSpanning && !b.isSpanning) {
          return 1;
        } else if (!a.isSpanning && b.isSpanning) {
          return -1;
        }

        return (
          new Date(a.dueDatetime).getTime() - new Date(b.dueDatetime).getTime()
        );
      });

    acc[key] = sortedActivities;
    return acc;
  }, {});
};

/**
 * Gets activities for a specific date and time slot
 */
export const getActivitiesForDateTime = (
  date: Date,
  time: string,
  activitiesByDateTime: Record<string, ScheduledActivity[]>
): ScheduledActivity[] => {
  const dateKey = format(date, 'yyyy-MM-dd');

  // Convert time string like "9:00 AM" to "9:00" format
  const timeParts = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!timeParts) return [];

  let hours = parseInt(timeParts[1]);
  const minutes = timeParts[2];
  const period = timeParts[3].toUpperCase();

  // Convert to 24-hour format
  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  const timeKey = `${hours}:${minutes}`;
  const key = `${dateKey}-${timeKey}`;

  return activitiesByDateTime[key] || [];
};

export const formatActivityTime = (
  dueDatetime: string,
  timezone: string
): string => {
  const date = parseISO(dueDatetime);
  return formatInTimeZone(date, timezone, 'h:mm a');
};

export const formatActivityDateTime = (
  dueDatetime: string,
  userTimezone: string,
  businessTimezone: string
): string => {
  const date = parseISO(dueDatetime);
  const userDate = formatInTimeZone(date, userTimezone, 'MMM d, yyyy h:mm a');

  if (userTimezone === businessTimezone) {
    return userDate;
  }

  const businessDate = formatInTimeZone(date, businessTimezone, 'h:mm a');
  return `${userDate} (${businessDate} in business timezone)`;
};

export const isWorkingDay = (date: Date): boolean => {
  const day = date.getDay();
  return day !== 0 && day !== 6; // 0 = Sunday, 6 = Saturday
};

export const filterWorkingDays = (dates: Date[]): Date[] => {
  return dates.filter(isWorkingDay);
};

export const isActivityToday = (activity: ScheduledActivity): boolean => {
  const activityDate = parseISO(activity.dueDatetime);
  const today = new Date();
  return isSameDay(activityDate, today);
};

export const getAssigneeDisplayName = (activity: ScheduledActivity): string => {
  if (activity.employee) {
    const name =
      activity.employee.fullName || activity.employee.displayName || '';
    const email = activity.employee.email ? `(${activity.employee.email})` : '';

    if (name) {
      return name;
    } else if (email) {
      return email;
    }
    return 'Unknown';
  }
  if (activity.role) {
    return activity.role.name;
  }
  return 'Unassigned';
};

export const formatAssociations = (
  activity: ScheduledActivity
): string | null => {
  if (
    !activity.associatedEntities ||
    activity.associatedEntities.length === 0
  ) {
    return null;
  }

  return activity.associatedEntities
    .map((entity) => `${entity.customObject.name}: ${entity.displayName}`)
    .join(', ');
};

export const formatAssociationsAsHtml = (
  activity: ScheduledActivity
): string | null => {
  if (
    !activity.associatedEntities ||
    activity.associatedEntities.length === 0
  ) {
    return null;
  }

  return activity.associatedEntities
    .map((entity) => {
      const url =
        entity.objectApiName === 'client_client'
          ? `/client/${entity.entityId}/details`
          : `/custom-objects/${entity.customObjectId}/${entity.entityId}/details`;
      return `${entity.customObject.objectName}: [${entity.displayName}](${url})`;
    })
    .join(', ');
};

export const getResponsiveView = (
  width: number,
  configuredView: 'day' | 'week' | 'month',
  isMobile: boolean
): 'day' | 'week' | 'month' => {
  if (isMobile || width < 6) {
    return 'day';
  }
  return configuredView;
};

export const getDateRangeForView = (
  date: Date,
  view: 'day' | 'week' | 'month',
  timezone: string
): { fromDate: string; toDate: string } => {
  let rangeStart: Date;
  let rangeEnd: Date;

  switch (view) {
    case 'month': {
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);
      rangeStart = addDays(monthStart, -getDay(monthStart));
      rangeEnd = addDays(monthEnd, 6 - getDay(monthEnd));
      break;
    }
    case 'week': {
      rangeStart = startOfWeek(date);
      rangeEnd = endOfWeek(date);
      break;
    }
    case 'day': {
      // Subtract 1 day from start to include activities that are spanning from the previous day
      rangeStart = addDays(startOfDay(date), -1);
      rangeEnd = endOfDay(date);
      break;
    }
    default:
      throw new Error(`Unknown calendar view: ${view}`);
  }

  return {
    fromDate: formatInTimeZone(rangeStart, timezone, 'yyyy-MM-dd'),
    toDate: formatInTimeZone(rangeEnd, timezone, 'yyyy-MM-dd'),
  };
};

export const encodeOptionValue = (
  calendarId: string,
  pluginApiName: string,
  sourceApiName: string
) => {
  const jsonValue = JSON.stringify({
    c: calendarId,
    p: pluginApiName,
    s: sourceApiName,
  });
  return Buffer.from(jsonValue).toString('base64');
};

export const decodeOptionValue = (encodedValue: string) => {
  const decodedJson = Buffer.from(encodedValue, 'base64').toString('utf-8');
  const parsed = JSON.parse(decodedJson);

  return {
    calendarId: parsed.c,
    pluginApiName: parsed.p,
    sourceApiName: parsed.s,
  };
};
